import { format } from "date-fns";
import { Link } from "react-router-dom";
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Menu,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Pagination,
  FormControl,
  FormLabel,
  Select,
  CircularProgress,
} from "@mui/material";

import { getJobDetailsRoute } from "../../../../../routers/routeUtils";
import RangedDatePicker from "../../../../../shared/Dates/RangedDatePicker";

import { HireJobItem } from "../../../api/types/job.types";
import { CountryAutocomplete, CountryOption } from "../../../../../shared/Form";
import { useGetHireJobs } from "../../../api/queries/useGetHireJobs";
import { GetHireJobsFilters } from "../../../api/services/job.services";
import { useUpdateJobStatus } from "../../../api/mutations/useUpdateJobStatus";
import { JobModeEnum, JobStatusEnum, jobModeList, jobStatusList } from "../../../enum/jobEnums";
import { CloseJobButtonIcon, JobMenuButtonIcon, SearchInputIcon } from "../../../assets/icons/jobs-overview-page-icons";
import { ListItemIcon } from "../../../assets/icons/go-hire-page-icons";

const JobTableList = () => {
  const { t } = useTranslation("goHire", { keyPrefix: "overviewPage" });
  const { t: tEnum } = useTranslation("goHire", { keyPrefix: "enum" });

  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const limit = 10;
  const offset = (page - 1) * limit;

  // Filter state
  const [filters, setFilters] = useState<{
    status?: JobStatusEnum;
    mode?: JobModeEnum;
    createdDateRange: [Date | null, Date | null];
    applyEndsDateRange: [Date | null, Date | null];
    countries: CountryOption[];
  }>({
    createdDateRange: [null, null],
    applyEndsDateRange: [null, null],
    countries: [],
  });

  // Build API filters from local state
  const apiFilters: GetHireJobsFilters = useMemo(() => {
    const apiFilter: GetHireJobsFilters = {
      q: searchQuery || undefined,
      status: filters.status,
      mode: filters.mode,
      createdFrom: filters.createdDateRange[0] ? format(filters.createdDateRange[0], "yyyy-MM-dd") : undefined,
      createdTo: filters.createdDateRange[1] ? format(filters.createdDateRange[1], "yyyy-MM-dd") : undefined,
      applyEndsFrom: filters.applyEndsDateRange[0] ? format(filters.applyEndsDateRange[0], "yyyy-MM-dd") : undefined,
      applyEndsTo: filters.applyEndsDateRange[1] ? format(filters.applyEndsDateRange[1], "yyyy-MM-dd") : undefined,
      countryIds: filters.countries.length > 0 ? filters.countries.map((c) => c.value).join(",") : undefined,
      offset: offset,
      limit: limit,
    };

    // Remove undefined values
    Object.keys(apiFilter).forEach((key) => {
      if (apiFilter[key as keyof GetHireJobsFilters] === undefined) {
        delete apiFilter[key as keyof GetHireJobsFilters];
      }
    });

    return apiFilter;
  }, [searchQuery, filters, offset, limit]);

  const { data, isLoading, isError } = useGetHireJobs(apiFilters);

  const handleClearFilters = () => {
    setFilters({
      createdDateRange: [null, null],
      applyEndsDateRange: [null, null],
      countries: [],
    });
    setSearchQuery("");
    setPage(1);
  };

  const hasActiveFilters = useMemo(() => {
    return (
      Boolean(filters.status) ||
      Boolean(filters.mode) ||
      Boolean(filters.createdDateRange[0]) ||
      Boolean(filters.createdDateRange[1]) ||
      Boolean(filters.applyEndsDateRange[0]) ||
      Boolean(filters.applyEndsDateRange[1]) ||
      Boolean(filters.countries.length > 0) ||
      Boolean(searchQuery)
    );
  }, [filters, searchQuery]);

  const tableColumns = [
    {
      label: t("jobs"),
      align: "left" as const,
    },
    {
      label: t("candidate"),
      align: "center" as const,
    },
    {
      label: t("status"),
      align: "center" as const,
    },
    {
      label: t("createdOn"),
      align: "center" as const,
    },
    {
      label: t("applyEndsOn"),
      align: "center" as const,
    },
    {
      label: null,
      align: "center" as const,
    },
  ];

  const totalPages = data?.meta ? data.meta.totalPages : 0;

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <Typography sx={styles.title}>{t("jobs")}</Typography>
        <TextField
          placeholder={t("search")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <SearchInputIcon />,
          }}
          sx={styles.search}
        />
      </Box>

      {/* Filters Section */}
      <Box sx={styles.filtersContainer}>
        <Box sx={styles.filtersRow}>
          <FormControl sx={styles.filterField}>
            <FormLabel>{t("status")}</FormLabel>
            <Select
              value={filters.status || ""}
              onChange={(e) => {
                setFilters({ ...filters, status: (e.target.value as JobStatusEnum) || undefined });
                setPage(1);
              }}
              sx={styles.filterSelect}
            >
              <MenuItem value="">{t("all")}</MenuItem>
              {jobStatusList.map((status) => (
                <MenuItem key={status} value={status}>
                  {tEnum(`jobStatus.${status}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={styles.filterField}>
            <FormLabel>{t("jobMode")}</FormLabel>
            <Select
              value={filters.mode || ""}
              onChange={(e) => {
                setFilters({ ...filters, mode: (e.target.value as JobModeEnum) || undefined });
                setPage(1);
              }}
              sx={styles.filterSelect}
            >
              <MenuItem value="">{t("all")}</MenuItem>
              {jobModeList.map((mode) => (
                <MenuItem key={mode} value={mode}>
                  {tEnum(`jobMode.${mode}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={styles.filterField}>
            <FormLabel>{t("countries")}</FormLabel>
            <CountryAutocomplete
              multiple
              limitTags={1}
              value={filters.countries}
              onChange={(value) => {
                setFilters({ ...filters, countries: (value as CountryOption[]) || [] });
                setPage(1);
              }}
              sx={styles.filterSelect}
            />
          </Box>
        </Box>

        <Box sx={styles.filtersRow}>
          <Box
            sx={{
              width: "fit-content",
            }}
          >
            <FormLabel>{t("createdDate")}</FormLabel>
            <RangedDatePicker
              dateRange={filters.createdDateRange}
              setDateRange={(dates) => {
                const newDates = typeof dates === "function" ? dates(filters.createdDateRange) : dates;
                setFilters({ ...filters, createdDateRange: newDates });
                setPage(1);
              }}
            />
          </Box>

          <Box
            sx={{
              width: "fit-content",
            }}
          >
            <FormLabel>{t("applyEndsDate")}</FormLabel>
            <RangedDatePicker
              dateRange={filters.applyEndsDateRange}
              setDateRange={(dates) => {
                const newDates = typeof dates === "function" ? dates(filters.applyEndsDateRange) : dates;
                setFilters({ ...filters, applyEndsDateRange: newDates });
                setPage(1);
              }}
            />
          </Box>
          {hasActiveFilters && (
            <Button onClick={handleClearFilters} variant="text-sm" sx={styles.clearFiltersButton}>
              {t("clear")}
            </Button>
          )}
        </Box>
      </Box>

      <Box sx={styles.tableWrapper}>
        {isLoading ? (
          <Box sx={styles.loadingContainer}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Box sx={styles.errorContainer}>
            <Typography>{t("errorLoadingJobs")}</Typography>
          </Box>
        ) : (
          <Table sx={styles.tableContainer}>
            <TableHead>
              <TableRow>
                {tableColumns.map(({ label, align }, index) => (
                  <TableCell key={label || index} align={align}>
                    <Box sx={{ textWrap: "nowrap" }}>{label}</Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.items && data.items.length > 0 ? (
                data.items.map((job) => <JobRow key={job.id} job={job} />)
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography>{t("noJobsFound")}</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Box>
      {data?.meta && totalPages > 1 && (
        <Box sx={styles.paginationContainer}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
            sx={{
              marginBottom: "45px",
              direction: "ltr !important",
              "& .MuiButtonBase-root": {
                borderRadius: "8px !important",
              },
              "& .MuiPagination-ul": {
                justifyContent: "center",
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default JobTableList;

type JobRowProps = {
  job: HireJobItem;
};

const JobRow = ({ job }: JobRowProps) => {
  const { t } = useTranslation("goHire", { keyPrefix: "overviewPage" });
  const { t: tJobMode } = useTranslation("goHire", { keyPrefix: "enum.jobMode" });
  const { t: tJobStatus } = useTranslation("goHire", { keyPrefix: "enum.jobStatus" });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);
  const handleClickMenu = (event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);

  const { mutate: updateJobStatus, isPending } = useUpdateJobStatus({
    onSuccess: handleCloseMenu,
  });

  const handleArchiveJob = () => {
    updateJobStatus({ jobId: job.id, status: JobStatusEnum.archived });
  };

  const handlePublishJob = () => {
    updateJobStatus({ jobId: job.id, status: JobStatusEnum.published });
  };

  // Determine which menu items to show based on current status
  const showArchiveOption = job.status !== JobStatusEnum.archived;
  const showPublishOption = job.status !== JobStatusEnum.published;

  const jobTitle = job.name;

  // Get location from first country in locationCountryIds
  const location: string | undefined =
    job?.jobLocationCountries && job?.jobLocationCountries?.length > 0
      ? job?.jobLocationCountries?.map((country) => country.name).join(", ")
      : undefined;

  // Format created date
  const createdDate = job.createdAt ? format(new Date(job.createdAt), "yyyy-MM-dd") : "-";

  const applyEndsDate = job.applyEndsAt ? format(new Date(job.applyEndsAt), "yyyy-MM-dd") : "-";
  const candidatesCount = job.candidates?.length;

  return (
    <TableRow>
      <TableCell>
        <Box sx={styles.jobsCellContainer} component={Link} to={getJobDetailsRoute(job.id)}>
          <Typography sx={styles.jobTitle} title={jobTitle}>
            {jobTitle}
          </Typography>
          <Box sx={styles.jobsCellDetailsContainer}>
            <Typography sx={styles.jobDetails}>{tJobMode(job.jobMode)}</Typography>
            {location && (
              <Typography sx={styles.jobDetails} title={location}>
                {" "}
                - {location}
              </Typography>
            )}
          </Box>
        </Box>
      </TableCell>
      <TableCell align="center">{candidatesCount}</TableCell>
      <TableCell align="center">
        <Box sx={styles.statusBadge(job.status === JobStatusEnum.archived || job.status === JobStatusEnum.draft)}>
          {tJobStatus(job.status)}
        </Box>
      </TableCell>
      <TableCell align="center" sx={styles.createdDate}>
        {createdDate}
      </TableCell>
      <TableCell align="center">{applyEndsDate}</TableCell>
      <TableCell align="center">
        <IconButton
          size="small"
          id="job-menu-button"
          aria-controls={openMenu ? "job-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={openMenu ? "true" : undefined}
          onClick={handleClickMenu}
        >
          <JobMenuButtonIcon />
        </IconButton>
        <Menu
          id="job-menu"
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleCloseMenu}
          slotProps={{
            paper: {
              "aria-labelledby": "job-menu-button",
            },
          }}
        >
          {showArchiveOption && (
            <MenuItem onClick={handleArchiveJob} sx={styles.closeJobMenuItem} disabled={isPending}>
              <CloseJobButtonIcon />
              {t("archiveJob")}
            </MenuItem>
          )}
          {showPublishOption && (
            <MenuItem onClick={handlePublishJob} sx={styles.closeJobMenuItem} disabled={isPending}>
              <ListItemIcon />
              {t("publishJob")}
            </MenuItem>
          )}
        </Menu>
      </TableCell>
    </TableRow>
  );
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    borderRadius: "12px",
    border: "1px solid var(--Gray-200, #EAECF0)",
    background: "var(--Base-White, #FFF)",
    width: "100%",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    padding: "20px 16px",
  },
  title: {
    flex: 1,
    fontFamily: "Montserrat",
    color: "var(--Gray-900, #101828)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  search: {
    width: "320px",
    borderRadius: "8px",
    border: "1px solid var(--Gray-300, #D0D5DD)",
    background: "var(--Base-White, #FFF)",
    boxShadow: "0 1px 2px 0 rgba(16, 24, 40, 0.05)",
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      gap: "8px",
    },
  },
  filtersContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "0 16px",
  },
  filtersRow: {
    display: "flex",
    gap: "16px",
    flexWrap: "wrap",
  },
  tableWrapper: {
    overflowX: "auto",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "40px",
  },
  errorContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "40px",
  },
  tableContainer: {
    minWidth: "600px",
    width: "100%",
    "& .MuiTableHead-root": {
      backgroundColor: "var(--Gray-50, #F9FAFB)",
    },
    "& .MuiTableCell-head": {
      borderTop: "1px solid var(--Gray-200, #EAECF0)",
      borderBottom: "1px solid var(--Gray-200, #EAECF0)",
      color: "var(--Gray-600, #475467)",
      fontFamily: "Montserrat",
      fontSize: "12px",
      fontStyle: "normal",
      fontWeight: 600,
      lineHeight: "18px",
      padding: "12px 16px",
    },
    "& .MuiTableCell-body": {
      borderBottom: "1px solid var(--Gray-200, #EAECF0)",
      padding: "12px 16px",
    },
    "& .MuiTableRow-root:last-child .MuiTableCell-body": {
      borderBottom: "none",
    },
  },
  jobTitle: {
    maxWidth: "350px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  jobDetails: {
    color: "var(--Dark---600, #707070)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "20px",
    whiteSpace: "nowrap",
    maxWidth: "240px",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  jobsCellContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    textDecoration: "none",
  },
  jobsCellDetailsContainer: {
    display: "flex",
    gap: "8px",
  },
  statusBadge: (isClosed: boolean) => ({
    display: "inline-flex",
    padding: "2px 8px",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "16px",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",
    fontFamily: "Montserrat",
    backgroundColor: isClosed ? "var(--Error-50, #FEF3F2)" : "var(--Success-50, #ECFDF3)",
    color: isClosed ? "var(--Error-700, #B42318)" : "var(--Success-700, #027A48)",
    whiteSpace: "nowrap",
  }),
  createdDate: {
    whiteSpace: "nowrap",
  },
  closeJobMenuItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "center",
    padding: "12px 16px 16px 16px",
    borderTop: "1px solid var(--Gray-200, #EAECF0)",
    "& .MuiPagination-root": {
      mb: 0,
    },
  },
  filterField: {
    flex: 1,
    minWidth: "200px",
  },
  clearFiltersContainer: {
    display: "flex",
    justifyContent: "flex-end",
  },
  filterSelect: {
    border: "1px solid var(--Gray-300, #D0D5DD)",
    background: "var(--Base-White, #FFF)",
    boxShadow: "0 1px 2px 0 rgba(16, 24, 40, 0.05)",
    borderRadius: "8px !important",
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px !important",
      gap: "8px",
    },
    "& .MuiSelect-select": {
      padding: "8px 16px",
    },
  },
  clearFiltersButton: {
    ml: "auto",
    p: 0,
    background: "transparent !important",
    backgroundColor: "transparent !important",
  },
};
